import ImageModal from "components/ImageModal";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { editCrimeReferences2 } from "services/incidentService";
import {
  CrimeReference,
  CrimeType,
  FILE_URL,
  ReferenceType,
  Reporter,
  Roles,
} from "types";
import { LoadingIcon, uploadIcon, viewIcon } from "utils/constant";
import {
  constructFileUrl,
  DateView,
  errorHandlerOnForm,
  hasRole,
} from "utils/helper";
import ReporterDetail from "./modals/ReporterDetail";
import ReporterForm from "./modals/ReporterForm";

type Props = {
  incident: CrimeReference | undefined;
  setRefetch: any;
  canManageIncidentDetail: boolean;
};

const IncidentInformationTab: React.FC<Props> = ({
  incident,
  setRefetch,
  canManageIncidentDetail = false,
}) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();
  const [actionIsLoading, setActionIsLoading] = useState(false);
  const [action, setAction] = useState<"Add" | "Edit">("Add");
  const [isOpenForm, setIsOpenForm] = React.useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = React.useState(false);

  const [selectedReporter, setSelectedReporter] = useState<Reporter>();
  // const [formState, setFormState] = useState<{
  //   decision_type: any;
  //   decision_date: Date | null;
  //   decision_details: string;
  // }>({
  //   decision_type: "pending",
  //   decision_date: null,
  //   decision_details: "",
  // });

  // useEffect(() => {
  //   if (incident) {
  //     setFormState({
  //       decision_type: incident?.decision_type || "pending",
  //       decision_date: incident?.decision_date || null,
  //       decision_details: incident?.decision_details || "",
  //     });
  //   }
  // }, [incident]);

  // const handleChange = (
  //   e: React.ChangeEvent<
  //     HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  //   >
  // ) => {
  //   const { name, value } = e.target;
  //   setFormState((prevState) => ({
  //     ...prevState,
  //     [name]: value,
  //   }));
  // };

  const handleFormClose = (refetch = false) => {
    setIsOpenForm(false);
    if (refetch) setRefetch((prev: any) => !prev);
  };

  return (
    <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
      {/* <div className="details">
        <span>Incident Details information</span>
        <p>Lorem Ipsum is a placeholder text commonly used in the design...</p>
      </div> */}

      <div className="">
        <div className="flex flex-row justify-between items-center ">
          {/* <button
            type="button"
            onClick={() => {}}
            className="text-[#E90102] px-6 py-3 rounded-md border border-[#E90102] hover:bg-[#E90102] hover:text-white font-semibold text-base"
          >
            Termination
          </button> */}

          <div className="align-middle pl-[55px] flex gap-2">
            {incident &&
              incident?.status === "pending" &&
              hasRole(loginData?.roles, [Roles.member], loginData) && (
                <button
                  className={`border border-[#212121] bg-white px-3 py-1  w-full
                                   text-black font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040] hover:text-white
                                     ${
                                       actionIsLoading
                                         ? "opacity-50 cursor-not-allowed"
                                         : ""
                                     }`}
                  disabled={actionIsLoading}
                  onClick={() => {
                    setActionIsLoading(true);
                    editCrimeReferences2(incident?.uu_id, {
                      status: "forwared_to_police_cordinator",
                    })
                      .then(() => {
                        showTooltip(t("successfully_submitted"), "success");
                        setRefetch((prev: any) => !prev);
                      })
                      .catch((error) => {
                        showTooltip(
                          error?.response?.data?.detail ||
                            errorHandlerOnForm(error),
                          "error",
                        );
                      })
                      .finally(() => setActionIsLoading(false));
                  }}
                >
                  {actionIsLoading ? (
                    <div className="flex items-center justify-center">
                      {LoadingIcon}
                      {t("loading")}
                    </div>
                  ) : (
                    t("forward_to_police_coordinator")
                  )}
                </button>
              )}
          </div>
        </div>

        <div className="px-4">
          <div className="grid grid-cols-3 gap-4 w-full">
            {[
              {
                property: t("incident_report_date"),
                value: DateView(incident?.date_received),
              },
              {
                property: t("incident_year"),
                value: incident?.year || "",
              },
              {
                property: t("incident_date"),
                value: incident?.crime_incidence_time
                  ? (() => {
                      // Expecting format YYYY-MM-DD
                      const date = incident?.crime_incidence_time;
                      if (typeof date === "string" && date.length >= 10) {
                        return date.slice(5, 10); // MM-DD
                      }
                      return "";
                    })()
                  : "",
              },
              {
                property: t("incident_time"),
                value: incident?.incident_timestamp || "",
              },
              {
                property: t("crime_type"),
                value: incident?.crime_type
                  ? CrimeType[incident?.crime_type]
                  : "",
              },
              {
                property: t("crime_category"),
                value: incident?.crime_category,
              },
              {
                property: t("reception_method"),
                value: incident?.reception_method?.reception_method,
              },
              {
                property: t("incident_registration_number"),
                value: incident?.Incident_Registration_number,
              },
              {
                property: t("reference_type"),
                value:
                  ReferenceType[
                    incident?.reference_type as keyof typeof ReferenceType
                  ],
              },
              {
                property: t("asset_reference"),
                value: incident?.asset_reference,
              },
              {
                property: t("decision_details"),
                value: incident?.decision_details,
              },
              {
                property: t("report_application_number"),
                value: incident?.report_application_number,
              },
              {
                property: t("incident_application_number"),
                value: incident?.incident_application_number,
              },
              {
                property: t("crime_narrative_file"),
                value: incident?.crime_naretive_file ? (
                  <div>
                    <a
                      href={incident?.crime_naretive_file?.toString()}
                      target="_blank"
                      className="rounded px-3 py-1 border bg-[#E90102] text-white text-sm flex gap-2"
                      rel="noreferrer"
                    >
                      {uploadIcon}
                      {t("crime_narrative_file")}
                    </a>
                  </div>
                ) : (
                  t("na")
                ),
              },
            ]?.map((info, index) => {
              return (
                <div key={index}>
                  <p className="font-medium font-[Poppins] text-sm text-[#E90101]">
                    {info.property}
                  </p>
                  <p className="font-normal text-sm text-black">{info.value}</p>
                </div>
              );
            })}
          </div>

          <p className="font-medium font-[Poppins] text-sm text-[#E90101] mt-7 mb-1">
            {t("crime_location")}
          </p>
          <div className="grid grid-cols-2 gap-4 w-[500px] bg-[#F9F9F9] p-7 rounded">
            {[
              {
                property: t("city"),
                value: incident?.crime_incidence_city,
              },
              {
                property: t("region"),
                value: incident?.crime_incidence_region,
              },
              {
                property: t("woreda"),
                value: incident?.crime_incidence_woreda,
              },
              {
                property: t("kebele"),
                value: incident?.crime_incidence_kebele,
              },
              {
                property: t("zone"),
                value: incident?.crime_incidence_zone,
              },
              {
                property: t("site"),
                value: incident?.crime_incidence_site,
              },
            ]?.map((info, index) => (
              <div key={index} className="flex flex-row gap-1">
                <p className="font-normal font-[Poppins] text-xs text-[#979797]">
                  {info.property}
                </p>
                <p
                  className="font-medium text-sm text-black"
                  style={{
                    overflowWrap: "break-word",
                  }}
                >
                  : {info.value}
                </p>
              </div>
            ))}
          </div>

          <p className="font-medium font-[Poppins] text-sm text-[#E90101] mt-3 mb-1">
            {t("incident_reported_by")}
          </p>

          {/* {incident && <ReporterTab incident={incident} />} */}

          {incident?.reporters && incident?.reporters.length === 0 && (
            <div>
              <p className="p-4 text-center text-gray-500">
                {t("no_reporter_found")}
              </p>
            </div>
          )}
          {incident?.reporters &&
            incident?.reporters?.map((reporter, index) => (
              <div>
                <div
                  key={index}
                  className="flex flex-row mt-4 border border-[#D4D4D4] p-4 rounded-lg"
                >
                  <div className="flex flex-1 items-center gap-2">
                    <img
                      src={(() => {
                        const doc = reporter?.documents?.find(
                          (doc) => doc.document_type === "reporter_picture",
                        );
                        return doc
                          ? constructFileUrl(doc, FILE_URL.REPORTER)
                          : require("../../../images/imageVector.png");
                      })()}
                      alt="User"
                      className="w-10 h-10 rounded mr-2 cursor-pointer"
                      onClick={() => {
                        setSelectedReporter(reporter);
                        setIsImageModalOpen(true);
                      }}
                    />
                    <div>
                      <p className="text-gray-800 font-semibold">{`${reporter?.first_name} ${reporter?.middle_name} ${reporter?.last_name}`}</p>
                      <div className="flex flex-row gap-2 items-center">
                        <p className="text-gray-500 text-sm">
                          {reporter?.age} {t("years_old_label")}
                        </p>
                        <p>•</p>
                        <p className="text-gray-500 text-sm">
                          {reporter?.education_level}
                        </p>
                        <p>•</p>
                        <p className="text-gray-500 text-sm">
                          {reporter?.nationality}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center mt-4 gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setSelectedReporter(reporter);
                      }}
                    >
                      {viewIcon}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          {canManageIncidentDetail && incident?.status !== "forwarded" && (
            <input
              type="button"
              className="border border-[#E90102] px-[76px] mt-2 py-1 text-[#E90102] font-[Poppins] font-[500] cursor-pointer text-base rounded-md hover:bg-[#E90102] hover:text-white transition duration-300 ease-in-out"
              value={t("add_reporter")}
              onClick={() => {
                setIsOpenForm(true);
                setAction("Add");
              }}
            />
          )}
        </div>

        {/* {hasRole(loginData?.roles, [Roles.prosecutor, Roles.member]) &&
          incident?.crime_type &&
          CrimeType[incident?.crime_type] === CrimeType.various_crime &&
          incident?.decision_type?.toString() === "pending" && (
            <div>
              <hr className="my-5" />
              <div className="grid grid-cols-2 gap-2 gap-x-4 mt-4">
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Decision Type
                  </label>
                  <select
                    className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                    name="decision_type"
                    value={formState?.decision_type}
                    onChange={handleChange}
                  >
                    <option value={""}>Select Decision Type</option>
                    {Object.entries(IncidentDecisionType).map(
                      ([key, value]) => (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      )
                    )}
                  </select>
                </div>

                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Decision Date
                  </label>
                  <input
                    type="date"
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300 w-[100%]"
                    name="decision_date"
                    value={
                      formState?.decision_date
                        ? new Date(formState.decision_date)
                            .toISOString()
                            .split("T")[0]
                        : ""
                    }
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="flex flex-col mt-1 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Decision Details
                </label>
                <textarea
                  placeholder="Decision details here ..."
                  rows={4}
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="decision_details"
                  value={formState?.decision_details}
                  onChange={handleChange}
                />
              </div>

              {incident?.decision_type?.toString() === "pending" && (
                <div className="flex flex-row mt-4 gap-3">
                  <input
                    type="button"
                    className="border border-[#212121] bg-[#212121] px-[76px] py-3 text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]"
                    value={"Submit Decision"}
                    onClick={() => {
                      if (!incident?.uu_id?.toString())
                        return showTooltip(t("incident_id_is_required"), "error");
                      if (
                        !formState?.decision_type ||
                        formState?.decision_type === "pending"
                      )
                        return showTooltip(
                          "Decision Type is required.",
                          "error"
                        );
                      if (!formState?.decision_date)
                        return showTooltip(
                          "Decision Date is required.",
                          "error"
                        );

                      updateDecision(
                        incident?.uu_id?.toString(),
                        formState?.decision_type,
                        formState?.decision_date
                      )
                        .then(() => {
                          showTooltip(t("successfully_submitted"), "success");
                          setRefetch(true);
                        })
                        .catch((error) => {
                          showTooltip(
                            error?.response?.data?.detail ||
                              errorHandlerOnForm(error) ||
                              errorHandler(error) ||
                              "Something went wrong. Please try again."
                          );
                        });
                    }}
                  />
                </div>
              )}
            </div>
          )}
        {incident?.decision_type?.toString() !== "pending" && (
          <div>
            <hr className="my-5" />

            <div className="flex gap-10">
              <div>
                <p className="font-medium font-[Poppins] text-sm text-[#E90101]">
                  Decision Type
                </p>
                <p className="font-normal text-sm text-black">
                  {incident?.decision_type
                    ? IncidentDecisionType[incident?.decision_type]
                    : ""}
                </p>
              </div>
              <div>
                <p className="font-medium font-[Poppins] text-sm text-[#E90101]">
                  Decision Date
                </p>
                <p className="font-normal text-sm text-black">
                  {incident?.decision_date
                    ? new Date(incident?.decision_date)?.toDateString()
                    : ""}
                </p>
              </div>
            </div>
            <div className="mt-2">
              <p className="font-medium font-[Poppins] text-sm text-[#E90101]">
                Decision Detail
              </p>
              <p className="font-normal text-sm text-black">
                {incident?.decision_details?.toString()}
              </p>
            </div>
          </div>
        )} */}
      </div>

      {incident && (
        <ReporterForm
          action={action}
          incident={incident}
          reporter={action === "Edit" ? selectedReporter : undefined}
          isOpen={isOpenForm}
          onClose={handleFormClose}
        />
      )}
      <ReporterDetail
        reporter={selectedReporter}
        isOpen={selectedReporter !== undefined && !isImageModalOpen}
        onClose={() => setSelectedReporter(undefined)}
      />
      <ImageModal
        isOpen={isImageModalOpen}
        imageSrc={(() => {
          const doc = selectedReporter?.documents?.find(
            (doc) => doc.document_type === "reporter_picture",
          );
          return doc
            ? constructFileUrl(doc, FILE_URL.REPORTER)
            : require("../../../images/imageVector.png");
        })()}
        label={
          selectedReporter?.first_name +
          " " +
          selectedReporter?.last_name +
          " " +
          selectedReporter?.last_name
        }
        onClose={() => {
          setIsImageModalOpen(false);
          setSelectedReporter(undefined);
        }}
      />
    </div>
  );
};

export default IncidentInformationTab;
